import { useState } from 'react';

import ErrorBoundary from 'components/ErrorBoundary';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';

import { HANDLED_GROUP_STATES } from '../constants';
import { HandledGroupState, HandledQuoteGroupsData } from '../types';
import { HandledQuoteGroup } from './HandledQuoteGroup';

type HandledQuoteGroupsProps = {
  groups: HandledQuoteGroupsData;
  selectedIds?: Set<number>;
  selectionEnabled?: boolean;
  onToggleLane?: (laneId: number) => void;
  onSelectLanes?: (laneIds: number[]) => void;
  onResetSelected?: () => void;
};

// Handled quote requests under the Kit suggestion list: one collapsed group per non-empty state.
// Read-only in milestone 1.
export function HandledQuoteGroups({
  groups,
  selectedIds = new Set(),
  selectionEnabled = false,
  onToggleLane = () => {},
  onSelectLanes = () => {},
  onResetSelected = () => {},
}: HandledQuoteGroupsProps) {
  const [expandedStates, setExpandedStates] = useState(
    () => new Set<HandledGroupState>()
  );
  const visibleStates = HANDLED_GROUP_STATES.filter(
    (state) => groups[state].length > 0
  );
  if (visibleStates.length === 0) {
    return null;
  }
  const allLaneIds = visibleStates.flatMap((state) =>
    groups[state].flatMap((batch) => batch.map((lane) => lane.id))
  );
  const allSelected =
    allLaneIds.length > 0 && allLaneIds.every((id) => selectedIds.has(id));
  const handleSelectAll = () => {
    setExpandedStates(allSelected ? new Set() : new Set(visibleStates));
    onSelectLanes(allLaneIds);
  };

  return (
    <Flex direction='col' gap='md' className='w-full'>
      {selectionEnabled && (
        <Flex justify='start' className='px-1'>
          <button
            type='button'
            className='cursor-pointer border-none bg-transparent'
            onClick={handleSelectAll}
          >
            <Typography
              variant='body-xxs'
              className='text-[11px] font-medium text-accent-700 hover:underline'
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </Typography>
          </button>
        </Flex>
      )}
      {visibleStates.map((state) => (
        // A broken group hides itself instead of taking down Kit.
        <ErrorBoundary key={state} fallback={null}>
          <HandledQuoteGroup
            state={state}
            batches={groups[state]}
            selectedIds={selectedIds}
            selectionEnabled={selectionEnabled}
            open={expandedStates.has(state)}
            onOpenChange={(open) =>
              setExpandedStates((current) => {
                const next = new Set(current);
                if (open) {
                  next.add(state);
                } else {
                  next.delete(state);
                }
                return next;
              })
            }
            onToggleLane={onToggleLane}
            onSelectLanes={onSelectLanes}
          />
        </ErrorBoundary>
      ))}
      {selectionEnabled && (
        <button
          type='button'
          className='w-full cursor-pointer rounded bg-brand-main px-3 py-1.5 text-xs font-medium text-neutral-50 transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50'
          disabled={selectedIds.size === 0}
          onClick={onResetSelected}
        >
          <Typography variant='body-xs' className='text-inherit'>
            {selectedIds.size > 0
              ? `Requote ${selectedIds.size} lanes`
              : 'Requote lanes'}
          </Typography>
        </button>
      )}
    </Flex>
  );
}
